<template>
  <div class="w-full">
    <div class="flex gap-3">
      <div
        ref="mapContainer"
        class="map-container relative z-0 flex-1 rounded-lg border"
        style="aspect-ratio: 4/3"
      ></div>

      <!-- Occupancy scale -->
      <div class="flex shrink-0 flex-col">
        <div
          v-for="(item, index) in legendItems"
          :key="item.pct"
          class="flex min-h-0 flex-1 gap-1.5"
        >
          <div
            class="w-4"
            :class="{
              'rounded-t': index === 0,
              'rounded-b': index === legendItems.length - 1,
            }"
            :style="{ backgroundColor: item.color }"
          ></div>
          <span
            class="self-center whitespace-nowrap text-xs tabular-nums text-gray-400"
            >{{ item.pct }}%</span
          >
        </div>
      </div>
    </div>

    <!-- Utilisation summary by pitch type -->
    <BaseTable
      v-if="pitchTypeSummary.length"
      :is-loading="false"
      key-field="pitchType"
      :rows="pitchTypeSummary"
      :columns="pitchTypeColumns"
      :footers="[pitchTypeTotals]"
      class="mt-4"
    />

    <!-- Pitch detail panel -->
    <div
      v-if="selectedPitch"
      class="mt-4 rounded-lg border bg-white p-4 shadow-sm"
    >
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-lg font-semibold">Pitch {{ selectedPitch.name }}</h3>
          <p class="text-sm text-gray-500">{{ selectedPitch.pitchType }}</p>
        </div>
        <button
          class="text-sm text-gray-400 hover:text-gray-600"
          @click="selectedPitch = null"
        >
          Close
        </button>
      </div>
      <div class="mt-3 grid grid-cols-3 gap-4">
        <div>
          <div class="text-xs uppercase text-gray-400">Days Occupied</div>
          <div class="text-xl font-bold">{{ selectedPitch.daysOccupied }}</div>
        </div>
        <div>
          <div class="text-xs uppercase text-gray-400">Period Days</div>
          <div class="text-xl font-bold">{{ selectedPitch.periodDays }}</div>
        </div>
        <div>
          <div class="text-xs uppercase text-gray-400">Occupancy</div>
          <div class="text-xl font-bold">{{ selectedPitch.occupancyPct }}%</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, onBeforeUnmount } from "vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import BaseTable from "@/components/BaseTable.vue";
import type { HeatmapPitchRow } from "@/services/reports/getHeatmapReport";
import type { ColumnDefinition } from "@/types/table";

// leaflet.heat is a side-effect plugin that extends the global L object.
// It must find L on window, and we must use dynamic import to avoid hoisting.
const loadHeatPlugin = async () => {
  (window as any).L = L;
  await import("leaflet.heat");
};

const props = defineProps<{
  siteMapImage: string;
  pitchData: HeatmapPitchRow[];
}>();

// Standard colour wheel gradient (blue → cyan → green → yellow → red)
// in 10% increments. HSL hue interpolated 240° → 0°.
const HEATMAP_GRADIENT: Record<number, string> = {
  0.0: "#0000ff",
  0.1: "#0066ff",
  0.2: "#00ccff",
  0.3: "#00ffcc",
  0.4: "#00ff66",
  0.5: "#00ff00",
  0.6: "#66ff00",
  0.7: "#ccff00",
  0.8: "#ffcc00",
  0.9: "#ff6600",
  1.0: "#ff0000",
};

const legendItems = Object.entries(HEATMAP_GRADIENT)
  .map(([pct, color]) => ({ pct: Math.round(parseFloat(pct) * 100), color }))
  .sort((a, b) => b.pct - a.pct);

type PitchTypeSummaryRow = {
  pitchType: string;
  pitchCount: number;
  daysOccupied: number;
  totalNights: number;
  utilisationPct: number;
};

const pitchTypeColumns: ColumnDefinition<PitchTypeSummaryRow>[] = [
  {
    label: "Pitch Type",
    name: "pitchType",
    getValue: (row) => row.pitchType,
  },
  {
    label: "Pitches",
    name: "pitchCount",
    align: "left",
    getValue: (row) => row.pitchCount,
  },
  {
    label: "Nights Occupied",
    name: "daysOccupied",
    align: "left",
    getValue: (row) => row.daysOccupied,
  },
  {
    label: "Utilisation",
    name: "utilisationPct",
    align: "left",
    getValue: (row) => `${row.utilisationPct}%`,
  },
];

const pitchTypeSummary = computed<PitchTypeSummaryRow[]>(() => {
  const groups = new Map<
    string,
    { pitchCount: number; daysOccupied: number; totalNights: number }
  >();

  for (const pitch of props.pitchData) {
    const key = pitch.pitchType || "Unknown";
    const group = groups.get(key) ?? {
      pitchCount: 0,
      daysOccupied: 0,
      totalNights: 0,
    };
    group.pitchCount += 1;
    group.daysOccupied += pitch.daysOccupied;
    group.totalNights += pitch.periodDays;
    groups.set(key, group);
  }

  return Array.from(groups.entries())
    .map(([pitchType, g]) => ({
      pitchType,
      pitchCount: g.pitchCount,
      daysOccupied: g.daysOccupied,
      totalNights: g.totalNights,
      utilisationPct: g.totalNights
        ? Math.round((g.daysOccupied / g.totalNights) * 100)
        : 0,
    }))
    .sort((a, b) => a.pitchType.localeCompare(b.pitchType));
});

const pitchTypeTotals = computed<PitchTypeSummaryRow>(() => {
  const totals = pitchTypeSummary.value.reduce(
    (acc, row) => {
      acc.pitchCount += row.pitchCount;
      acc.daysOccupied += row.daysOccupied;
      acc.totalNights += row.totalNights;
      return acc;
    },
    { pitchCount: 0, daysOccupied: 0, totalNights: 0 },
  );
  return {
    pitchType: "Total",
    ...totals,
    utilisationPct: totals.totalNights
      ? Math.round((totals.daysOccupied / totals.totalNights) * 100)
      : 0,
  };
});

const mapContainer = ref<HTMLElement>();
const selectedPitch = ref<HeatmapPitchRow | null>(null);

const BASE_RADIUS = 35;
const BASE_BLUR = 20;

let map: L.Map | null = null;
let heatPluginLoaded = false;
let currentHeatLayer: L.HeatLayer | null = null;
let initialZoom = 0;
const pitchMarkers: L.Circle[] = [];
// Bumped whenever the map is (re)initialised or torn down, so async work
// started against an old map (image onload, plugin import) can bail out.
let mapGeneration = 0;

// Tear the map down safely. Leaflet 1.9.4's ScrollWheelZoom.removeHooks()
// removes the wheel listener but does NOT clear its debounce timer, so a
// scroll-zoom queued in the ~40ms before teardown fires _performZoom after
// map.remove() and reads _leaflet_pos off the deleted _mapPane
// (GlitchTip camc-frontend #1494). Clear the pending timer first.
const destroyMap = () => {
  if (!map) return;
  clearTimeout((map.scrollWheelZoom as unknown as { _timer?: number })._timer);
  map.remove();
  map = null;
};

const clearLayers = () => {
  if (currentHeatLayer && map) {
    map.removeLayer(currentHeatLayer);
    currentHeatLayer = null;
  }
  pitchMarkers.forEach((marker) => {
    if (map) map.removeLayer(marker);
  });
  pitchMarkers.length = 0;
};

const renderHeatmap = async (pitchData: HeatmapPitchRow[]) => {
  if (!map || !pitchData.length) return;

  if (!heatPluginLoaded) {
    await loadHeatPlugin();
    heatPluginLoaded = true;
    // The map may have been torn down while the plugin import was in flight.
    if (!map) return;
  }

  clearLayers();

  const maxOccupancy = Math.max(...pitchData.map((p) => p.occupancyPct), 1);

  const heatPoints: [number, number, number][] = pitchData
    .filter((p) => p.occupancyPct > 0)
    .map((p) => [p.mapY, p.mapX, p.occupancyPct / maxOccupancy]);

  const scale = Math.pow(2, map.getZoom() - initialZoom);
  currentHeatLayer = L.heatLayer(heatPoints, {
    radius: Math.round(BASE_RADIUS * scale),
    blur: Math.round(BASE_BLUR * scale),
    maxZoom: map.getZoom(),
    max: 1.0,
    gradient: HEATMAP_GRADIENT,
  });
  currentHeatLayer.addTo(map);

  pitchData.forEach((pitch) => {
    const marker = L.circle([pitch.mapY, pitch.mapX], {
      radius: 8,
      weight: 0,
      fillColor: "transparent",
      fillOpacity: 0,
      interactive: true,
    });

    marker.bindTooltip(`Pitch ${pitch.name}: ${pitch.occupancyPct}%`, {
      direction: "top",
      offset: [0, -10],
    });

    marker.on("click", () => {
      selectedPitch.value = pitch;
    });

    // @ts-ignore
    marker.addTo(map);
    pitchMarkers.push(marker);
  });
};

const initMap = () => {
  const generation = ++mapGeneration;
  const img = new Image();

  img.onload = async function () {
    // The image loads asynchronously: the component may have unmounted, or
    // the map image changed and re-initialised, while we waited. Without
    // this guard L.map(null) throws "Map container not found".
    if (generation !== mapGeneration || !mapContainer.value) return;

    const imageWidth = img.width;
    const imageHeight = img.height;

    const imageBounds: [[number, number], [number, number]] = [
      [0, 0],
      [(800 / imageWidth) * imageHeight, 800],
    ];

    map = L.map(mapContainer.value!, {
      crs: L.CRS.Simple,
      zoomControl: true,
      attributionControl: false,
      zoomSnap: 0,
      zoomDelta: 0.5,
      maxBounds: imageBounds,
      maxBoundsViscosity: 1.0,
    });

    // @ts-ignore
    L.imageOverlay(props.siteMapImage, imageBounds).addTo(map);
    // @ts-ignore
    map.fitBounds(imageBounds);
    initialZoom = map.getZoom();

    map.on("zoomend", () => {
      if (!currentHeatLayer || !map) return;
      const scale = Math.pow(2, map.getZoom() - initialZoom);
      currentHeatLayer.setOptions({
        radius: Math.round(BASE_RADIUS * scale),
        blur: Math.round(BASE_BLUR * scale),
        maxZoom: map.getZoom(),
      });
    });

    if (props.pitchData.length) {
      await renderHeatmap(props.pitchData);
    }
  };

  img.src = props.siteMapImage;
};

watch(
  () => props.pitchData,
  async (newData) => {
    if (map && newData) {
      await renderHeatmap(newData);
    }
  },
);

watch(
  () => props.siteMapImage,
  () => {
    clearLayers();
    destroyMap();
    selectedPitch.value = null;
    initMap();
  },
);

onMounted(() => {
  initMap();
});

onBeforeUnmount(() => {
  mapGeneration++;
  destroyMap();
});
</script>

<style scoped>
.map-container :deep(.leaflet-container) {
  background-color: #f9fafb;
}

.map-container :deep(.leaflet-image-layer) {
  filter: grayscale(1);
}

/* leaflet.heat renders onto a canvas in the overlay pane — dim it so the
   underlying site map stays legible through the heat overlay. */
.map-container :deep(.leaflet-overlay-pane canvas) {
  opacity: 0.6;
}

:deep(.leaflet-popup-content-wrapper) {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

:deep(.leaflet-popup-content) {
  margin: 0;
}
</style>
